import { useEffect, useState } from 'react';
import { BsChevronDown } from 'react-icons/bs';
import { HiX } from 'react-icons/hi';
import { useDispatch, useSelector } from 'react-redux';

import useCheckScreen from '@core/blocks/eduResults/hooks/useCheckScreen';
import useElementEvents from '@core/hooks/cohesion/useElementEvents';
import {
  setIsComparisonModalOpen,
  selectIsComparisonBannerOpen,
  selectProgramsToCompare,
  removeProgramFromCompareList,
} from '@core/reducers/matchesSlice';
import { VoyagerResult } from '@core/ts/results';
import cn from '@core/utils/classNames';

import styles from './JoComparisonBanner.module.css';

const JoComparisonBanner = ({ squareButtons = false }) => {
  const { elementClicked } = useElementEvents();

  const dispatch = useDispatch();
  const programsToCompare = useSelector(selectProgramsToCompare);

  const isBannerVisible = useSelector(selectIsComparisonBannerOpen);
  const [showErrorText, setShowErrorText] = useState(false);
  const [isBannerExpanded, setIsBannerExpanded] = useState(true);
  const [animate, setAnimate] = useState(false);

  const handleClickCompare = () => {
    if (programsToCompare.length < 2) {
      setShowErrorText(true);
    } else {
      setShowErrorText(false);
      dispatch(setIsComparisonModalOpen(true));
    }
    elementClicked({
      webElement: {
        elementType: 'compare-feature',
        text: 'See Comparison',
        location: 'compare-voyager',
        htmlId: 'see-comparison-cta',
        name: 'see-comparison',
      },
    });
    if (showErrorText) {
      setAnimate(true);
      const timer = setTimeout(() => setAnimate(false), 500); // reset after animation
      return () => clearTimeout(timer);
    }
    return null;
  };

  const handleClickClearSelection = () => {
    programsToCompare.forEach((program) => {
      dispatch(removeProgramFromCompareList(program));
    });
    elementClicked({
      webElement: {
        elementType: 'compare-feature',
        text: 'Clear Selection',
        location: 'compare-voyager',
        htmlId: 'clear-selection-button',
        name: 'clear-selection',
      },
    });
  };

  const handleClickRemoveProgram = (program: VoyagerResult) => {
    dispatch(removeProgramFromCompareList(program));
    elementClicked({
      webElement: {
        elementType: 'compare-feature',
        text: program?.school?.name,
        location: 'compare-voyager',
        htmlId: 'x-remove-school',
        name: 'x-remove-school',
      },
    });
  };

  const handleBannerExpansion = () => {
    setIsBannerExpanded(!isBannerExpanded);
  };

  const { isMobile } = useCheckScreen();
  useEffect(() => {
    if (!isMobile) {
      setIsBannerExpanded(true);
    }
  }, [isMobile]);

  useEffect(() => {
    setIsBannerExpanded(true);
    if (programsToCompare.length > 1) {
      setShowErrorText(false);
    }
  }, [programsToCompare.length]);

  useEffect(() => {
    if (showErrorText) {
      setAnimate(true);
      const timer = setTimeout(() => setAnimate(false), 500); // reset after animation
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [showErrorText]);

  if (!isBannerVisible) {
    return null;
  }

  return (
    <section
      id="joComparisonBanner"
      className={`${styles.joComparisonBanner} ${isBannerExpanded ? styles.expanded : styles.collapsed}`}
    >
      <button className={styles.expandButton} type="button" onClick={handleBannerExpansion}>
        <BsChevronDown
          size={20}
          style={{
            transform: !isBannerExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease',
          }}
        />
      </button>
      <div className={styles.contentWrapper}>
        <div className={styles.content}>
          <p className={`${styles.instructionText} ${animate && isMobile ? styles.shake : ''}`}>
            {showErrorText && isMobile ? (
              <span className={`${styles.errorText}`}>Choose at least 2 schools to compare</span>
            ) : (
              'Add up to three schools to compare'
            )}
          </p>
          <div className={styles.selectedSchools}>
            {[...programsToCompare, ...Array(3 - programsToCompare.length).fill(null)].map((program, index) => (
              <div
                className={styles.selectedSchoolContainer}
                key={program?.id ? `${program?.id}-${index}` : `placeholder-${index}`}
              >
                {program ? (
                  <>
                    <div className={styles.schoolLogo}>
                      <button
                        className={styles.removeProgramIcon}
                        type="button"
                        onClick={() => handleClickRemoveProgram(program)}
                        aria-label={`Remove ${program?.school?.name || 'school'} from comparison`}
                      >
                        <HiX size={16} />
                      </button>
                      <img
                        src={
                          program?.school?.slug
                            ? `https://simple-storage-server.highereducation.com/${program?.school.slug}.png?w=130&h=80`
                            : undefined
                        }
                        alt={`${program?.school?.name} logo` || 'Placeholder'}
                      />
                    </div>
                  </>
                ) : (
                  <div className={styles.schoolPlaceholder} />
                )}
              </div>
            ))}
          </div>
        </div>
        <div className={styles.buttonsContainer}>
          {showErrorText && !isMobile && (
            <p className={`${styles.instructionText} ${styles.errorText} ${animate && !isMobile ? styles.shake : ''}`}>
              Choose at least 2 schools to compare
            </p>
          )}
          <button
            onClick={handleClickClearSelection}
            className={cn(styles.clearButton, squareButtons && styles.squareButton)}
            type="button"
          >
            Clear Selection
          </button>
          <button
            onClick={handleClickCompare}
            className={cn(styles.compareButton, squareButtons && styles.squareButton)}
            type="button"
          >
            See Comparison
          </button>
        </div>
      </div>
    </section>
  );
};

export default JoComparisonBanner;
